import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Space,
  Breadcrumb,
  Alert,
  Spin,
  Table,
  Tag,
  Select,
} from 'antd';
import { HomeOutlined, AppstoreOutlined } from '@ant-design/icons';
import { TimeRangeSelector } from '../components/dashboard/TimeRangeSelector';
import { useJobs } from '../api/queries/useJobs';
import { useNamespaces } from '../api/queries/useNamespaces';
import { useTenant } from '../hooks/useTenant';
import type { JobSummary, TimeRange, RolledUpJobStatus } from '../api/types';
import { getErrorMessage } from '../utils/error';
import { getJobStatusTagColor, getJobStatusLabel } from '../utils/jobStatus';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

function formatDuration(ms: number | undefined): string {
  if (ms === undefined) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const dur = dayjs.duration(ms);
  return `${Math.floor(dur.asMinutes())}m ${dur.seconds()}s`;
}

export function JobsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tenantId, isLoading: isTenantLoading } = useTenant();

  // Parse URL params
  const statusParam = searchParams.get('status') as RolledUpJobStatus | undefined;
  const namespaceParam = searchParams.get('namespace') || undefined;
  const timeRangeParam = (searchParams.get('time_range') as TimeRange) || '24h';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  // Local state
  const [status, setStatus] = useState<RolledUpJobStatus | undefined>(statusParam);
  const [namespace, setNamespace] = useState<string | undefined>(namespaceParam);
  const [timeRange, setTimeRange] = useState<TimeRange>(timeRangeParam);
  const [page, setPage] = useState(pageParam);
  const [pageSize, setPageSize] = useState(50);

  // Fetch namespaces for dropdown
  const { data: namespacesData } = useNamespaces({ time_range: timeRange });

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (namespace) params.set('namespace', namespace);
    params.set('time_range', timeRange);
    params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [status, namespace, timeRange, page, setSearchParams]);

  // Fetch jobs
  const { data, isLoading, error } = useJobs({
    status,
    namespace,
    time_range: timeRange,
    page,
    page_size: pageSize,
  });

  const handleStatusChange = (value: string) => {
    setStatus(value ? (value as RolledUpJobStatus) : undefined);
    setPage(1);
  };

  const handleNamespaceChange = (value: string | undefined) => {
    setNamespace(value || undefined);
    setPage(1);
  };

  const handleTimeRangeChange = (newTimeRange: TimeRange) => {
    setTimeRange(newTimeRange);
    setPage(1);
  };

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const columns = [
    {
      title: 'Job ID',
      dataIndex: 'job_id',
      key: 'job_id',
      render: (jobId: string) => (
        <a onClick={() => navigate(`/jobs/${jobId}`)}>{jobId.substring(0, 12)}...</a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getJobStatusTagColor(status)}>{getJobStatusLabel(status)}</Tag>
      ),
    },
    {
      title: 'Namespace',
      dataIndex: 'namespace',
      key: 'namespace',
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      render: (time: string) =>
        time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      render: (time: string | undefined) =>
        time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: 'Duration',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: formatDuration,
    },
    {
      title: 'Trace ID',
      dataIndex: 'trace_id',
      key: 'trace_id',
      render: (traceId: string) => (
        <Text copyable={{ text: traceId }}>
          {traceId.substring(0, 12)}...
        </Text>
      ),
    },
  ];

  if (isTenantLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <Alert
        type="warning"
        message="No Tenant Selected"
        description="Please select a tenant from the header."
        showIcon
      />
    );
  }

  const hasMore = data?.pagination.has_more || false;
  const total = hasMore ? page * pageSize + 1 : (page - 1) * pageSize + (data?.jobs.length || 0);

  return (
    <div>
      <Breadcrumb
        items={[
          {
            href: '/',
            title: (
              <>
                <HomeOutlined />
                <span>Dashboard</span>
              </>
            ),
          },
          {
            title: (
              <>
                <AppstoreOutlined />
                <span>Jobs</span>
              </>
            ),
          },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={3} style={{ margin: 0 }}>
          Jobs
        </Title>

        <Space wrap>
          <Select
            style={{ width: 150 }}
            placeholder="Filter by status"
            value={status || ''}
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
          />

          <Select
            style={{ width: 200 }}
            placeholder="Filter by namespace"
            value={namespace}
            onChange={handleNamespaceChange}
            allowClear
            showSearch
            options={namespacesData?.namespaces.map((ns) => ({
              value: ns,
              label: ns,
            }))}
          />

          <TimeRangeSelector
            value={timeRange}
            onChange={handleTimeRangeChange}
          />
        </Space>

        {error && (
          <Alert
            type="error"
            message="Error loading jobs"
            description={getErrorMessage(error)}
            showIcon
          />
        )}

        <Table<JobSummary>
          dataSource={data?.jobs || []}
          columns={columns}
          rowKey="job_id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: handlePageChange,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['25', '50', '100'],
          }}
        />
      </Space>
    </div>
  );
}
